import styled from '@emotion/styled';
import { tokens, Button, IconButton, Loader, Tooltip } from '@hotjar/hotsauce';
import { useCallback, useMemo } from 'react';
import { maxBy } from 'lodash-es';
import type { AddFilterTippyInstance } from './AddFilter/AddFilter';
import { AddFilter } from './AddFilter/AddFilter';
import type {
  FilterChainConfig,
  FilterType,
  GlobalFilter,
  SingleFilterState,
  UpsellType,
  FilterKindEnum,
} from '../../domains/global-filters/model/FilterChain';
import { getFilterMetadata } from './FilterChainUtils';
import { DateRangeFilter } from './SingleFilter/DateRangeFilter/DateRangeFilter';
import { SingleFilterWrapper } from './SingleFilterWrapper';
import {
  applyDateConstraint,
  isDateRangeBeforeMinDate,
} from './SingleFilter/DateRangeFilter/DateRangeFilterUtils';

const ContainerDiv = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: ${tokens.spacer.x1};
  align-items: center;
`;

const SeparatorDiv = styled.div`
  height: ${tokens.spacer.x3};
  width: 2px;
  min-width: 2px;
  background-color: ${tokens.color.neutral_200};
  border-radius: ${tokens.radius.m};
`;

// We put an hard limit because it doesn't make sense to give infinite amount to the user and we avoid future problem with API limitations.
const MAX_NUMBER_OF_FILTERS = 50;

function initFilterObjects(
  currentFilterList: FilterType[],
  config: FilterChainConfig,
): [Map<FilterKindEnum, SingleFilterState>, FilterType[]] {
  const filterByKind = new Map<FilterKindEnum, SingleFilterState>();
  const usableFilterList: FilterType[] = [];
  config.actionableFilterList.forEach((item) => {
    const { kind } = item;
    const oneFilterState = {
      kind: item.kind,
      metaData: getFilterMetadata(kind),
      config: item,
      isUsed: false,
    };

    filterByKind.set(kind, oneFilterState);
  });

  currentFilterList.forEach((item) => {
    const { kind } = item;
    const oneFilterState = filterByKind.get(kind);
    if (oneFilterState && oneFilterState.config.isLocked !== true) {
      oneFilterState.isUsed = true;
      usableFilterList.push(item);
    }
  });
  return [filterByKind, usableFilterList];
}

interface FilterChainProps {
  currentValue: GlobalFilter;
  isFiltersDropdownVisibleByDefault?: boolean;
  autoFocusAddFilter?: boolean;
  onChange: (newValue: GlobalFilter) => void;
  onSaveSegment?: (currentValue: GlobalFilter, saveAsNew?: boolean) => void;
  onUpsellClicked?: (upsellType: UpsellType) => void;
  config: FilterChainConfig;
  onDropdownFilterMenuShown?: () => void;
  setAddFilterTippyInstance?: (tippyInstance: AddFilterTippyInstance | null) => void;
  isScaleTrial?: boolean;
}

export function FilterChain({
  currentValue,
  isFiltersDropdownVisibleByDefault,
  autoFocusAddFilter,
  onChange,
  onSaveSegment,
  onUpsellClicked,
  config,
  onDropdownFilterMenuShown,
  setAddFilterTippyInstance,
  isScaleTrial,
}: FilterChainProps): JSX.Element {
  const [filterStateByKind, usableFilterList] = useMemo(
    () => initFilterObjects(currentValue.filterList, config),
    [currentValue.filterList, config],
  );

  const handleOnChange = useCallback(
    (filtersInUiFormat: GlobalFilter) => {
      if (filtersInUiFormat.dateRange) {
        const activeDateConstraintList = config.actionableFilterList
          .filter((oneFilterConfig) =>
            filtersInUiFormat.filterList.some((item) => item.kind === oneFilterConfig.kind),
          )
          .map((item) => item.dateConstraint);
        // At the moment it should only come from clicked element filter if present
        const activeDateConstraint = maxBy(activeDateConstraintList, (item) => item?.minDate);

        if (activeDateConstraint) {
          // We have to make sure the date range is after the min date for clicked element data collection
          if (isDateRangeBeforeMinDate(filtersInUiFormat.dateRange, activeDateConstraint)) {
            // We have to modify the date range
            const newDateRange = applyDateConstraint(
              filtersInUiFormat.dateRange,
              activeDateConstraint,
            );
            return onChange({
              ...filtersInUiFormat,
              dateRange: newDateRange,
            });
          }
        }
        // There isn't any clicked element data so we need to check if the date was autoselected before
        else if (filtersInUiFormat.dateRange.autoSelectedConstraint != null) {
          // We remove the constraint
          const newDateRange = {
            ...filtersInUiFormat.dateRange,
            autoSelectedConstraint: undefined,
          };
          return onChange({
            ...filtersInUiFormat,
            dateRange: newDateRange,
          });
        }
      }
      return onChange(filtersInUiFormat);
    },
    [config.actionableFilterList, onChange],
  );

  return (
    <ContainerDiv data-testid="filter-chain">
      {config.isLoading === true && <Loader size="m" />}
      {config.isLoading !== true && (
        <>
          {config.hideDateRangeFilter !== true && (
            <DateRangeFilter
              currentValue={currentValue.dateRange}
              onApply={(newValue) => {
                handleOnChange({
                  ...currentValue,
                  dateRange: newValue,
                });
              }}
              hideAllDatesOption={config.hideAllDateOption}
              dateConstraint={config.dateConstraint}
              useToggleGroupDatePicker={config.useToggleGroupDatePicker}
            />
          )}
          {config.hideAllFilters !== true && (
            <>
              {usableFilterList.map((singleFilterValue, index) => (
                <SingleFilterWrapper
                  key={singleFilterValue.id}
                  currentValue={singleFilterValue}
                  onApply={(newSingleFilterValue) => {
                    handleOnChange({
                      ...currentValue,
                      filterList: [
                        ...usableFilterList.slice(0, index),
                        newSingleFilterValue,
                        ...usableFilterList.slice(index + 1),
                      ],
                    });
                  }}
                  onRemove={() => {
                    handleOnChange({
                      ...currentValue,
                      filterList: [
                        ...usableFilterList.slice(0, index),
                        ...usableFilterList.slice(index + 1),
                      ],
                    });
                  }}
                  filterStateByKind={filterStateByKind}
                  isScaleTrial={isScaleTrial}
                  isFunnelStep={config.isFunnelStep}
                />
              ))}

              {config.hideAddFilters !== true && (
                <AddFilter
                  filterStateByKind={filterStateByKind}
                  onNewFilter={(newSingleFilter) => {
                    handleOnChange({
                      ...currentValue,
                      filterList: [...usableFilterList, newSingleFilter],
                    });
                  }}
                  isMaxReached={usableFilterList.length >= MAX_NUMBER_OF_FILTERS}
                  showUpsellFooter={config.showUpsellFooter}
                  onUpsellClicked={onUpsellClicked}
                  onDropdownFilterMenuShown={onDropdownFilterMenuShown}
                  setAddFilterTippyInstance={setAddFilterTippyInstance}
                  usePrimaryStyledAddFilters={config.usePrimaryStyledAddFilters}
                  isFiltersDropdownVisibleByDefault={isFiltersDropdownVisibleByDefault}
                  autoFocusAddFilter={autoFocusAddFilter}
                  dateRange={currentValue.dateRange}
                  isScaleTrial={isScaleTrial}
                  isFunnelStep={config.isFunnelStep}
                />
              )}
            </>
          )}
          {config.hideClearAllFilters !== true &&
            config.hideAllFilters !== true &&
            usableFilterList.length > 0 && (
              <IconButton
                tooltipText="Clear all filters"
                kind="ghost"
                icon="close"
                onClick={() =>
                  handleOnChange({
                    ...currentValue,
                    filterList: [],
                  })
                }
                description="Clear all filters"
                size="s"
              />
            )}
          {config.showSaveSegmentButton === true && (
            <>
              <SeparatorDiv />
              <Tooltip text="Write access required" isDisabled={config.hasFilterSavePermission}>
                <div>
                  <Button
                    kind="ghost"
                    onClick={() => onSaveSegment?.(currentValue)}
                    aria-label="Save segment"
                    size="s"
                    isDisabled={config.hasFilterSavePermission !== true}
                  >
                    Save segment
                  </Button>
                </div>
              </Tooltip>
            </>
          )}
        </>
      )}
    </ContainerDiv>
  );
}
